import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Building2, Search, Plus, Edit2, Trash2, ExternalLink, Users, 
  HandHeart, DollarSign, Download, Filter, RefreshCw, Check, X
} from 'lucide-react';

const TIPOS_PARCEIRO = ['Empresa', 'Fundação', 'Associação', 'Organização'];
const AREAS_ATIVIDADE = [
  'ENTRAJUDA', 'Formação', 'Bolsa do Voluntariado', 'Projetos Solidários',
  'Banco de Bens Doados', 'Apoio à Gestão e Organização', 'Banco de Equipamentos'
];

const ParceirosPage = () => {
  const [parceiros, setParceiros] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterAtivo, setFilterAtivo] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingParceiro, setEditingParceiro] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    nif: '',
    tipo_parceiro: 'Empresa',
    areas_atividade: ['ENTRAJUDA'],
    website: '',
    email: '',
    telefone: '',
    morada: '',
    pessoa_contacto: '',
    descricao: '',
    contribui_voluntarios: false,
    contribui_doacoes: false,
    num_voluntarios_cedidos: 0,
    valor_doacoes_total: 0
  });

  useEffect(() => {
    fetchParceiros();
    fetchStats();
  }, []);

  const fetchParceiros = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/parceiros');
      setParceiros(response.data);
    } catch (error) {
      toast.error('Erro ao carregar parceiros');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/parceiros/stats/resumo');
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleImportEntrajuda = async () => {
    try {
      const response = await apiClient.post('/parceiros/import-entrajuda');
      toast.success(response.data.message);
      fetchParceiros();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao importar parceiros');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingParceiro) {
        await apiClient.put(`/parceiros/${editingParceiro.id}`, formData);
        toast.success('Parceiro atualizado com sucesso');
      } else {
        await apiClient.post('/parceiros', formData);
        toast.success('Parceiro criado com sucesso');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchParceiros();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao guardar parceiro');
    }
  };

  const handleEdit = (parceiro) => {
    setEditingParceiro(parceiro);
    setFormData({
      nome: parceiro.nome || '',
      nif: parceiro.nif || '',
      tipo_parceiro: parceiro.tipo_parceiro || 'Empresa',
      areas_atividade: parceiro.areas_atividade || ['ENTRAJUDA'],
      website: parceiro.website || '',
      email: parceiro.email || '',
      telefone: parceiro.telefone || '',
      morada: parceiro.morada || '',
      pessoa_contacto: parceiro.pessoa_contacto || '',
      descricao: parceiro.descricao || '',
      contribui_voluntarios: parceiro.contribui_voluntarios || false,
      contribui_doacoes: parceiro.contribui_doacoes || false,
      num_voluntarios_cedidos: parceiro.num_voluntarios_cedidos || 0,
      valor_doacoes_total: parceiro.valor_doacoes_total || 0
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem a certeza que deseja desativar este parceiro?')) return;
    try {
      await apiClient.delete(`/parceiros/${id}`);
      toast.success('Parceiro desativado');
      fetchParceiros();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao desativar parceiro');
    }
  };

  const resetForm = () => {
    setEditingParceiro(null);
    setFormData({
      nome: '',
      nif: '',
      tipo_parceiro: 'Empresa',
      areas_atividade: ['ENTRAJUDA'],
      website: '',
      email: '',
      telefone: '',
      morada: '',
      pessoa_contacto: '',
      descricao: '',
      contribui_voluntarios: false,
      contribui_doacoes: false,
      num_voluntarios_cedidos: 0,
      valor_doacoes_total: 0
    });
  };

  const toggleArea = (area) => {
    const current = formData.areas_atividade || [];
    if (current.includes(area)) {
      setFormData({ ...formData, areas_atividade: current.filter(a => a !== area) });
    } else {
      setFormData({ ...formData, areas_atividade: [...current, area] });
    }
  };

  const filteredParceiros = parceiros.filter(p => {
    const matchSearch = p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       p.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchArea = !filterArea || p.areas_atividade?.includes(filterArea);
    const matchAtivo = filterAtivo === '' || 
                      (filterAtivo === 'true' && p.ativo) || 
                      (filterAtivo === 'false' && !p.ativo);
    return matchSearch && matchArea && matchAtivo;
  });

  return (
    <div className="space-y-6" data-testid="parceiros-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Parceiros</h1>
          <p className="text-slate-600 dark:text-slate-400">Gestão de entidades parceiras da ENTRAJUDA</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleImportEntrajuda} data-testid="import-parceiros-btn">
            <Download className="w-4 h-4 mr-2" />
            Importar ENTRAJUDA
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} data-testid="add-parceiro-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Parceiro
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>{editingParceiro ? 'Editar Parceiro' : 'Novo Parceiro'}</DialogTitle>
                  <DialogDescription>
                    {editingParceiro ? 'Atualize os dados do parceiro' : 'Preencha os dados do novo parceiro'}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome *</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        required
                        data-testid="parceiro-nome-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nif">NIF</Label>
                      <Input
                        id="nif"
                        value={formData.nif}
                        onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tipo_parceiro">Tipo</Label>
                      <Select value={formData.tipo_parceiro} onValueChange={(v) => setFormData({ ...formData, tipo_parceiro: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_PARCEIRO.map(tipo => (
                            <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        value={formData.telefone}
                        onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="morada">Morada</Label>
                    <Input
                      id="morada"
                      value={formData.morada}
                      onChange={(e) => setFormData({ ...formData, morada: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pessoa_contacto">Pessoa de Contacto</Label>
                    <Input
                      id="pessoa_contacto"
                      value={formData.pessoa_contacto}
                      onChange={(e) => setFormData({ ...formData, pessoa_contacto: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Áreas de Atividade</Label>
                    <div className="flex flex-wrap gap-2">
                      {AREAS_ATIVIDADE.map(area => (
                        <Badge
                          key={area}
                          variant={formData.areas_atividade?.includes(area) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleArea(area)}
                        >
                          {formData.areas_atividade?.includes(area) && <Check className="w-3 h-3 mr-1" />}
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="descricao">Descrição</Label>
                    <Textarea
                      id="descricao"
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="contribui_voluntarios"
                        checked={formData.contribui_voluntarios}
                        onCheckedChange={(checked) => setFormData({ ...formData, contribui_voluntarios: checked })}
                      />
                      <Label htmlFor="contribui_voluntarios">Contribui com voluntários</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="contribui_doacoes"
                        checked={formData.contribui_doacoes}
                        onCheckedChange={(checked) => setFormData({ ...formData, contribui_doacoes: checked })}
                      />
                      <Label htmlFor="contribui_doacoes">Contribui com doações</Label>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="save-parceiro-btn">
                    {editingParceiro ? 'Atualizar' : 'Criar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Total Parceiros</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Ativos</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.ativos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Voluntários Cedidos</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {stats.contribuicoes?.total_voluntarios || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Total Doações</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  €{(stats.contribuicoes?.total_doacoes || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-parceiros-input"
                />
              </div>
            </div>
            <Select value={filterArea || "all"} onValueChange={(v) => setFilterArea(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Área de Atividade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as áreas</SelectItem>
                {AREAS_ATIVIDADE.map(area => (
                  <SelectItem key={area} value={area}>{area}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterAtivo || "all"} onValueChange={(v) => setFilterAtivo(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="true">Ativos</SelectItem>
                <SelectItem value="false">Inativos</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setSearchTerm(''); setFilterArea(''); setFilterAtivo(''); }}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Parceiros List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Parceiros</CardTitle>
          <CardDescription>{filteredParceiros.length} parceiros encontrados</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin" />
            </div>
          ) : filteredParceiros.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum parceiro encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredParceiros.map(parceiro => (
                <div
                  key={parceiro.id}
                  className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  data-testid={`parceiro-item-${parceiro.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#0066CC]/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-[#0066CC]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900 dark:text-white">{parceiro.nome}</h3>
                        <Badge variant={parceiro.ativo ? 'default' : 'secondary'}>
                          {parceiro.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                        <Badge variant="outline">{parceiro.tipo_parceiro}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {parceiro.areas_atividade?.slice(0, 3).map(area => (
                          <span key={area} className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                            {area}
                          </span>
                        ))}
                        {parceiro.areas_atividade?.length > 3 && (
                          <span className="text-xs text-slate-500">+{parceiro.areas_atividade.length - 3}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {parceiro.email && <span>{parceiro.email}</span>}
                        {parceiro.contribui_voluntarios && (
                          <span className="flex items-center gap-1">
                            <HandHeart className="w-3 h-3" /> Voluntários
                          </span>
                        )}
                        {parceiro.contribui_doacoes && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> Doações
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {parceiro.website && (
                      <Button variant="ghost" size="icon" asChild>
                        <a href={parceiro.website} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(parceiro)} data-testid={`edit-parceiro-${parceiro.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(parceiro.id)} data-testid={`delete-parceiro-${parceiro.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ParceirosPage;
